import { Grid, IconButton, InputAdornment, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/system';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { Remove } from '@mui/icons-material';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { MemberAdvancedSearchAddProfile } from 'src/modules/member/components/memberAdvancedSearchAddProfile';
import { MemberProfileData } from 'src/modules/member/models/member';
import { useGlobals } from 'src/shared/contexts/appContext';
import { getMainSetting } from 'src/shared/functions/getMainSetting';
import { MainSettingsEnum } from 'src/shared/models/enums/mainSettings.enum';
import { CurrencyEnum } from 'src/shared/models/enums/currency.enum';
import { PaymentMethodEnum } from 'src/shared/models/enums/paymentMethod.enum';
import { AssociationFeeRequest } from '../models/associationFeeRequest';

type AssociationFeeProfileHandlerProps = {
  openAddprofile: boolean;
  setOpenAddprofile: (openProfiles: boolean) => void;
  readOnlyMode: boolean;
  paymentMethod?: PaymentMethodEnum;
  autogiroFee?: boolean;
};

export const AssociationFeeProfileHandler = (props: AssociationFeeProfileHandlerProps) => {
  const { openAddprofile, setOpenAddprofile, readOnlyMode, paymentMethod = undefined, autogiroFee = undefined } = props;
  const { t } = useTranslation();
  const { currentUser } = useGlobals();
  const currency = getMainSetting(currentUser?.mainSettings ?? [], MainSettingsEnum.Currency, CurrencyEnum.SEK);

  const { control } = useFormContext<AssociationFeeRequest>();
  const { fields, append, remove } = useFieldArray<AssociationFeeRequest, 'feeBasedOnProfile', 'memberProfileItem_id'>({
    control,
    name: 'feeBasedOnProfile',
  });

  const handleProfileAdded = (newProfiles: MemberProfileData[]) => {
    const existingIds: number[] = fields.map((p) => p.memberProfileItem_id);
    const filteredNewProfiles = newProfiles.filter((profile) => !existingIds.includes(profile.memberProfileItem_id));

    filteredNewProfiles.forEach((profile) => {
      append({
        memberProfileItem_id: profile.memberProfileItem_id,
        profileGroup: profile.groupName,
        profile: profile.itemName,
        fee: 0,
        feeAutogiro: paymentMethod && paymentMethod === PaymentMethodEnum.MainAndFamily ? 0 : undefined,
      });
    });
    setOpenAddprofile(false);
  };

  return (
    <>
      <Grid container mb={2}>
        {/* Rubriker För Större skärmar */}
        <Grid item xs={12} md={2.5} sx={{ display: { xs: 'none', md: 'block' } }}>
          <Typography variant='body1' sx={{ fontWeight: 600 }}>
            {t('association:profileGroup')}
          </Typography>
        </Grid>

        <Grid item xs={12} md={2.5} sx={{ display: { xs: 'none', md: 'block' } }}>
          <Typography variant='body1' sx={{ fontWeight: 600 }}>
            {t('association:profile')}
          </Typography>
        </Grid>
      </Grid>

      {fields.map((field, index) => {
        return (
          <Grid container key={field.memberProfileItem_id} mb={2} spacing={2}>
            <Grid item xs={12} md={2.5}>
              {/* Rubrik och Profilgrupp För Mindre skärmar */}
              <Box sx={{ display: { xs: 'flex', md: 'none' }, gap: 1 }}>
                <Typography variant='body1' sx={{ fontWeight: 600 }}>
                  {t('association:profileGroup')}:
                </Typography>
                <Typography variant='body1'>{field.profileGroup}</Typography>
              </Box>

              {/* Profilgrupp För större skärmar */}
              <Typography variant='body1' sx={{ display: { xs: 'none', md: 'block' } }}>
                {field.profileGroup}
              </Typography>
            </Grid>

            <Grid item xs={12} md={2.5}>
              {/* Rubrik och Profilnamn För Mindre skärmar */}
              <Box sx={{ display: { xs: 'flex', md: 'none' }, gap: 1, mb: 1 }}>
                <Typography variant='body1' sx={{ fontWeight: 600 }}>
                  {t('association:profile')}:
                </Typography>
                <Typography variant='body1'>{field.profile}</Typography>
              </Box>

              {/* Profilnamn För större skärmar */}
              <Typography variant='body1' sx={{ display: { xs: 'none', md: 'block' } }}>
                {field.profile}
              </Typography>
            </Grid>

            <Grid item xs={12} md={7}>
              <Grid container sx={{ marginLeft: { xs: undefined } }} spacing={2}>
                <Grid item xs={readOnlyMode ? 12 : 10.5} md={readOnlyMode ? 6 : 5.5} sx={{ mb: { xs: 2, md: 0 } }}>
                  <ControlledTextField
                    disabled={readOnlyMode}
                    label={t('association:paymentMethodFieldLabels.feeProfile')}
                    type='number'
                    name={`feeBasedOnProfile.${index}.fee`}
                    InputProps={{
                      endAdornment: (
                        <>
                          <InputAdornment position='end'>{currency}</InputAdornment>
                        </>
                      ),
                    }}
                  />
                </Grid>
                {autogiroFee && paymentMethod && paymentMethod === PaymentMethodEnum.MainAndFamily && (
                  <Grid item xs={readOnlyMode ? 12 : 10.5} md={readOnlyMode ? 6 : 5.5}>
                    <ControlledTextField
                      disabled={readOnlyMode}
                      label={t('Avgift för profil - Autogiro')}
                      type='number'
                      name={`feeBasedOnProfile.${index}.feeAutogiro`}
                      InputProps={{
                        endAdornment: (
                          <>
                            <InputAdornment position='end'>{currency}</InputAdornment>
                          </>
                        ),
                      }}
                    />
                  </Grid>
                )}
                {!readOnlyMode && (
                  <Grid item xs={0.5} sx={{ mt: { xs: autogiroFee && paymentMethod === PaymentMethodEnum.MainAndFamily ? -7 : 0, md: 0 } }}>
                    <IconButton color='error' onClick={() => remove(index)}>
                      <Remove fontSize='small' />
                    </IconButton>
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>
        );
      })}

      <GenericDialog
        open={openAddprofile}
        showDialogActions={false}
        dialogTitle={t('association:addProfile')}
        maxWidth='sm'
        dialogContent={
          <Box mb={2}>
            <MemberAdvancedSearchAddProfile
              propName='addProfile'
              callback={(val) => {
                handleProfileAdded(val);
              }}
              closeEdit={() => setOpenAddprofile(false)}
            />
          </Box>
        }
        onClose={() => setOpenAddprofile(false)}
        onSave={() => ConsoleHelper.log()}
      />
    </>
  );
};
